import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cuttingService } from "../services/cuttingService";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const CuttingApplications = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Extract status from path: /admin/cutting/total -> total
  const pathParts = location.pathname.split("/");
  const status = pathParts[pathParts.length - 1]; // total, approved, pending, rejected
  const [cuttings, setCuttings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadCuttings();
  }, [status, currentPage, user]);

  const loadCuttings = async () => {
    setLoading(true);
    try {
      let response;
      // Use role-based endpoints from /userdashboard or /permissions based on role
      const role = user?.role?.toLowerCase() || "";
      
      // Use role-based endpoints for cutting applications
      if (status === "total") {
        // Total applications: /api/userdashboard/total?page=1&limit=20
        response = await api.get("/userdashboard/total", {
          params: { page: currentPage, limit: 50 },
        });
      } else if (status === "approved" || status === "approve") {
        // Approved applications: /api/userdashboard/approve?page=1&limit=20
        response = await api.get("/userdashboard/approve", {
          params: { page: currentPage, limit: 50 },
        });
      } else if (status === "pending") {
        // Pending applications: /api/userdashboard/pending?page=1&limit=20
        response = await api.get("/userdashboard/pending", {
          params: { page: currentPage, limit: 50 },
        });
      } else if (status === "rejected") {
        // Rejected applications: /api/userdashboard/rejected?page=1&limit=20
        response = await api.get("/userdashboard/rejected", {
          params: { page: currentPage, limit: 50 },
        });
      } else {
        // Default: all cuttings - use role-based endpoint
        response = await api.get("/userdashboard/total", {
          params: { page: currentPage, limit: 50 },
        });
      }

      if (response.data) {
        if (response.data.data) {
          setCuttings(response.data.data);
        }
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else if (response.data.totalPages) {
          setPagination({
            page: response.data.currentPage || currentPage,
            limit: response.data.pageSize || 50,
            total: response.data.totalRecords || 0,
            totalPages: response.data.totalPages || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error loading cuttings:", error);
      alert("Error loading applications: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "total":
        return "Total Applications";
      case "approved":
      case "approve":
        return "Approved Applications";
      case "pending":
        return "Pending Applications";
      case "rejected":
        return "Rejected Applications";
      default:
        return "Cutting Applications";
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">{getStatusTitle()}</h2>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sr. No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Permission ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Permission Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Purpose
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Division
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Applied On
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cuttings.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  cuttings.map((cutting, index) => (
                    <tr key={cutting.permission_id || cutting.id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * 50 + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {cutting.permission_id || cutting.id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cutting.name || cutting.user_name || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cutting.Permission_Type || "Cutting Permission"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cutting.purpose ? cutting.purpose.replace(/_/g, " ") : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cutting.division || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            (cutting.status || cutting.permission_status) === "Approved" ||
                            (cutting.status || cutting.permission_status) === "approved"
                              ? "bg-blue-100 text-blue-800"
                              : (cutting.status || cutting.permission_status) === "Rejected" ||
                                (cutting.status || cutting.permission_status) === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {cutting.status || cutting.permission_status || "Raised"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cutting.created_at
                          ? new Date(cutting.created_at).toLocaleDateString("en-GB")
                          : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => navigate(`/admin/cutting/view/${cutting.permission_id || cutting.id}`, { replace: false })}
                          className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(currentPage - 1) * 50 + 1} to{" "}
                {Math.min(currentPage * 50, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))
                  }
                  disabled={currentPage >= pagination.totalPages}
                  className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CuttingApplications;

